import React from 'react';
import styled, { css } from 'styled-components';
import {
  fontSize,
  maxBreakpointQuery,
  minBreakpointQuery,
  standardColours,
  standardTransition,
  visuallyHidden,
} from '../styles';
import { ArrowButton, Button, Container, Heading } from './ui';

const StyledPlanTabs = styled.div`
  ${minBreakpointQuery.mlarge`
    display: none;
  `}
`;

const StyledContainer = styled(Container)`
  ${maxBreakpointQuery.mlarge`
    padding-left: 0;
    padding-right: 0;
  `}
`;

const StyledHeader = styled.header`
  ${maxBreakpointQuery.mlarge`
    padding-left: 30px;
    padding-right: 30px;
  `}
`;

const StyledOverline = styled.p`
  ${fontSize(18)}
  margin-bottom: 15px;
`;

const StyledHeading = styled(Heading)``;

const StyledTabs = styled.div``;

const StyledList = styled.ul`
  display: flex;
  margin-top: 30px;
`;

// moved up for last child
const StyledTab = styled.button`
  width: 100%;
  padding: 15px 0;
  background: none;
  border: none;
  border-top: 1px solid #d6d6d6;
  border-bottom: 1px solid #d6d6d6;
  border-right: 1px solid #d6d6d6;
  transition: ${standardTransition('background-color')};

  ${({ active }) => {
    if (active) {
      return css`
        background-color: #f1f1f1;
      `;
    }
  }};
`;

const StyledItem = styled.li`
  flex-grow: 1;

  &:first-child {
    ${StyledTab} {
      border-left: 1px solid #d6d6d6;
    }
  }
`;

const StyledContent = styled.article`
  margin-top: 35px;

  ${maxBreakpointQuery.mlarge`
    padding-left: 30px;
    padding-right: 30px;
  `}

  ${({ active }) => {
    if (!active) {
      return css`
        ${visuallyHidden()};
      `;
    }
  }}
`;

const StyledContentIntro = styled.div`
  text-align: center;
`;

const StyledPlanHeading = styled.h3`
  ${fontSize(25)}
  line-height: 1.4;
`;

const StyledPlanPriceHeading = styled.p`
  margin-top: 12px;
`;

const StyledButton = styled(Button)`
  margin-top: 20px;
`;

const StyledFeatures = styled.div`
  margin-top: 35px;
`;

const StyledFeatureGroup = styled.div`
  margin-top: 20px;
`;

const StyledFeatureCategory = styled.h4`
  ${fontSize(18)}
`;

const StyledFeatureList = styled.ul`
  margin-top: 20px;
`;

const StyledFeatureItem = styled.li`
  display: flex;
  align-items: center;
  margin: 15px 0;
`;

const StyledTick = styled.span`
  position: relative;
  width: 25px;
  height: 25px;
  display: inline-block;
  background-color: #8dd400;
  border-radius: 50%;
  margin-right: 15px;

  &:after {
    content: '';
    position: absolute;
    left: 10px;
    top: 6px;
    width: 6px;
    height: 11px;
    border: solid ${standardColours.white};
    border-width: 0 2px 2px 0;
    -webkit-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
  }
`;

const StyledFooter = styled.footer`
  text-align: center;
`;

const StyledFeaturesButton = styled(ArrowButton)`
  margin-top: 20px;
  background: none;

  &:hover {
    background: none;
  }
`;

const PlanTabs = ({
  overline,
  heading,
  plansLink,
  plans,
  featureCategories,
  features,
  activeTabId,
  setActiveTabId,
}) => (
  <StyledPlanTabs>
    <StyledContainer>
      <StyledHeader>
        {overline && <StyledOverline>{overline}</StyledOverline>}
        <StyledHeading>{heading}</StyledHeading>
      </StyledHeader>
      <StyledTabs>
        <StyledList>
          {plans.map(({ name }, id) => (
            <StyledItem key={id}>
              <StyledTab
                active={id === activeTabId}
                onClick={() => {
                  setActiveTabId(id);
                }}
              >
                {name}
              </StyledTab>
            </StyledItem>
          ))}
        </StyledList>
        {plans.map(({ name, heading, subheading, ctaLink }, id) => {
          // Find features availble for this plan by category
          let planFeatures = [];

          features.categories.forEach(features => {
            features.nodes.forEach(feature => {
              const result = feature.plans.filter(plan => {
                return plan.plan.name === name && plan.includedInPlan;
              });

              if (result.length > 0) {
                planFeatures[feature.category.title]
                  ? planFeatures[feature.category.title].features.push(feature)
                  : (planFeatures[feature.category.title] = {
                      features: [feature],
                    });
              }
            });
          });

          const sortedPlanFeatures = Object.entries(planFeatures).sort(
            (a, b) =>
              featureCategories.findIndex(({ title }) => title === a[0]) -
              featureCategories.findIndex(({ title }) => title === b[0])
          );

          return (
            <StyledContent key={id} active={id === activeTabId}>
              <StyledContentIntro>
                <StyledPlanHeading>{name}</StyledPlanHeading>
                <StyledPlanPriceHeading
                  dangerouslySetInnerHTML={{
                    __html: heading.replace(/(<p>|<\/p>)/g, ''),
                  }}
                />
                <StyledButton to={ctaLink.pageUrl}>{ctaLink.text}</StyledButton>
              </StyledContentIntro>
              <StyledFeatures>
                {sortedPlanFeatures.map((featureCategory, id) => (
                  <StyledFeatureGroup key={id}>
                    <StyledFeatureCategory>
                      {featureCategory[0]}
                    </StyledFeatureCategory>
                    <StyledFeatureList>
                      {featureCategory[1].features.map(({ title }, id) => (
                        <StyledFeatureItem key={id}>
                          <StyledTick />
                          {title}
                        </StyledFeatureItem>
                      ))}
                    </StyledFeatureList>
                  </StyledFeatureGroup>
                ))}
              </StyledFeatures>
            </StyledContent>
          );
        })}
        {plansLink && (
          <StyledFooter>
            <StyledFeaturesButton to={plansLink.pageUrl}>
              {plansLink.text}
            </StyledFeaturesButton>
          </StyledFooter>
        )}
      </StyledTabs>
    </StyledContainer>
  </StyledPlanTabs>
);

export default PlanTabs;
